// The external pages an item's identifiers lead to (TMDB, IMDb; Open Library,
// the BnF and Google Books for books): identifier fields of those schemes,
// and the text fields older types keep TMDB and IMDb ids in (`id_tmdb`,
// `tmdb_id`, `id_imdb`, `imdb_id`, from the completion agents).
import { fieldKey } from '../../fieldKeys';

const LEGACY_KEYS = { id_tmdb: 'tmdb', tmdb_id: 'tmdb', id_imdb: 'imdb', imdb_id: 'imdb' };
const VALID = {
  tmdb: /^\d{1,12}$/, imdb: /^tt\d{7,10}$/, igdb: /^\d{1,12}$/,
  olid: /^OL[1-9]\d{0,11}M$/, bnf: /^cb\d{8,9}[0-9bcdfghjkmnpqrstvwxz]$/, gbooks: /^[A-Za-z0-9_-]{12}$/,
};
/** Schemes whose ids keep their case (Open Library's are upper-case, Google's mixed). */
const CASED = { olid: (id) => id.toUpperCase(), gbooks: (id) => id };

/** 'tmdb', 'imdb', 'olid', 'bnf', 'gbooks' or null: the source a field's value identifies an item in. */
export const sourceOf = (field) => {
  if (!field) return null;
  if (field.type === 'identifier' && VALID[field.scheme]) return field.scheme;
  return LEGACY_KEYS[fieldKey(field)] || null;
};

/** The id, cleaned, when the value is one of the source's; null otherwise. */
export const sourceId = (source, value) => {
  const text = String(value ?? '').trim();
  const id = CASED[source] ? CASED[source](text) : text.toLowerCase();
  return VALID[source] && VALID[source].test(id) ? id : null;
};

/**
 * TMDB's kind of page for a type: the model's (movie, tv); a plain type
 * whose name says series (série, serie, tv) is TV, any other a film — the
 * rule bin/backfill-covers.php uses.
 */
export const tmdbKindOf = (type) => {
  if (!type) return 'movie';
  if (type.preset === 'tv') return 'tv';
  if (type.preset === 'movie') return 'movie';
  const name = String(type.name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return /seri|\btv\b/.test(name) ? 'tv' : 'movie';
};

/** The page of an id on its source, or null. */
export const sourceUrl = (source, id, tmdbKind = 'movie') => {
  if (!id) return null;
  if (source === 'tmdb') return `https://www.themoviedb.org/${tmdbKind}/${encodeURIComponent(id)}`;
  if (source === 'imdb') return `https://www.imdb.com/title/${encodeURIComponent(id)}/`;
  if (source === 'olid') return `https://openlibrary.org/books/${encodeURIComponent(id)}`;
  if (source === 'bnf') return `https://catalogue.bnf.fr/ark:/12148/${encodeURIComponent(id)}`;
  if (source === 'gbooks') return `https://books.google.com/books?id=${encodeURIComponent(id)}`;
  return null;
};

/** The first valid id of each source among an item's used fields: {tmdb, imdb, olid, bnf, gbooks} (null when none). */
export const sourceIds = (fields, values) => {
  const out = { tmdb: null, imdb: null, olid: null, bnf: null, gbooks: null, igdb: null };
  (fields || []).forEach((field) => {
    const source = sourceOf(field);
    if (source && !out[source]) out[source] = sourceId(source, values[fieldKey(field)]);
  });
  return out;
};

/**
 * A game's IGDB page (phase 4 §4b.4): IGDB addresses games by their slug, not
 * their id, so the page is the item's igdb_url when it is one of IGDB's.
 */
export const igdbPage = (values) => {
  const url = String((values && values.igdb_url) || '').trim();
  return /^https:\/\/www\.igdb\.com\/games\/[a-z0-9-]+$/.test(url) ? url : null;
};

/** A field whose name is only its key ("id_tmdb"): shown with a readable name instead. */
export const rawName = (field) => {
  const name = String(field.name || '');
  return name === fieldKey(field) || /^[a-z0-9]+(_[a-z0-9]+)+$/i.test(name);
};
